import { NgModule }             from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { DashboardComponent }   from './dashboard/dashboard.component';
import { DashboardHomeComponent } from './inspector-dashboard/dashboard-home.component';
import { CalendarComponent } from './calendar/calendar.component';
import { InspectionListComponent } from './inspection-list/inspection-list.component';
import { HomeComponent }   from './home/home.component';
import { LoginComponent }   from './login/login.component';
import { AuthGuard } from './extras/auth.guard';
import { GroupWidgetsComponent } from './group-widgets/group-widgets.component'

import {SotableWidgetComponent} from './shared/components/sotable-widget/sotable-widget.component'
import { NewAppWidgetComponent } from './shared/components/new-app-widget/new-app-widget.component'

import { ReportSettingsComponent } from './components/report-settings/report-settings.component';
import { InspectionReportSettingComponent } from './components/inspection-report-setting/inspection-report-setting.component';
import { BillingReportSettingComponent } from './components/billing-report-setting/billing-report-setting.component';
import { DiagramComponent } from './components/diagram/diagram.component';
import { ChatMainComponent } from './modules/chat/chat-main/chat-main.component';
import { ChatAuthComponent } from './modules/chat/chat-auth/chat-auth.component';
import { ChatAuthGuard } from './modules/chat/guards/chat-auth.guard';
import { PromptsComponent } from './modules/chat/prompts/prompts.component';
import { ReportLibraryComponent } from './report-library/report-library.component';
import { SettingsPageComponent } from './settings/settings-page.component';
import { ChemicalsComponent } from './settings/chemicals/chemicals.component';
import { LeadSourcesComponent } from './settings/lead-sources/lead-sources.component';
import { BranchesComponent } from './settings/branches/branches.component';
import { RolesPermissionsComponent } from './settings/roles-permissions/roles-permissions.component';
import { UsersListComponent } from './settings/users/users-list.component';
import { UserFormComponent } from './settings/users/user-form.component';
import { ChangePasswordComponent } from './settings/change-password/change-password.component';
import { AutoEmailsComponent } from './settings/auto-emails/auto-emails.component';
import { SmtpSettingsComponent } from './settings/auto-emails/smtp-settings.component';
import { DataExchangePageComponent } from './settings/data-exchange/data-exchange-page.component';
import { SystemFormComponent } from './settings/system-form/system-form.component';
import { SignIntegrationComponent } from './settings/sign-integration/sign-integration.component';
import { LockoutSettingsComponent } from './settings/lockout/lockout-settings.component';
const routes: Routes = [
	{ path: 'login', component: LoginComponent },
  	{path: '', canActivate: [AuthGuard], children: [
      { path: '', redirectTo: '/dashboard', pathMatch: 'full' },
      { path: 'home', component: HomeComponent },
      // Inspectors get the inspector dashboard, other roles the administrator one (DashboardHomeComponent);
      // the old dashboard and its reports stay at /dashboard-classic.
      { path: 'dashboard', component: DashboardHomeComponent },
      // Old Welcome page links: the Guided Tour opens its introduction over ?next= (no page here).
      { path: 'welcome', children: [] },
      { path: 'dashboard-classic', component: DashboardComponent },
      // Replaces twin-ci's calendar pages; who sees which jobs is enforced by api/calendar/*.
      { path: 'calendar', component: CalendarComponent, data: { title: 'Calendar' } },
      // Replaces twin-ci's inspections_list pages; who sees which jobs is enforced by api/inspections-list/*.
      // (development branch: add canActivate: [FeatureGuard], data.feature 'dashboard.search' when merging.)
      { path: 'inspections-list', component: InspectionListComponent, data: {} },
      // Report Library: the sidebar's Reports entry opens it (mockup reports).
      { path: 'reports', component: ReportLibraryComponent, data: { title: 'Reports' } },
      // Settings workspace (mockup settings): the sidebar's Settings entry opens it. /settings/<key> are
      // the settings pages whose design is not built yet (blank pages, see nav-menu.ts).
      { path: 'settings', component: SettingsPageComponent, data: { title: 'Settings' } },
      // Pages cut from twin-ci System Settings (settings/system-form).
      // General Settings (mockup): fee charge date, tab order, report workflow and whole dollar
      // rounding on one page; the old pages' URLs open it.
      { path: 'settings/inspection-general', component: SystemFormComponent, data: { title: 'Settings', page: 'inspection-general' } },
      { path: 'settings/amount-rounding', redirectTo: 'settings/inspection-general' },
      { path: 'settings/fee-charge-date', redirectTo: 'settings/inspection-general' },
      { path: 'settings/tab-order', redirectTo: 'settings/inspection-general' },
      { path: 'settings/report-workflow', redirectTo: 'settings/inspection-general' },
      { path: 'settings/weekly-report', component: SystemFormComponent, data: { title: 'Settings', page: 'weekly-report' } },
      { path: 'settings/time-calculator', component: SystemFormComponent, data: { title: 'Settings', page: 'time-calculator' } },
      { path: 'settings/pricing', component: SystemFormComponent, data: { title: 'Settings', page: 'pricing' } },
      { path: 'settings/agent-outreach', component: SystemFormComponent, data: { title: 'Settings', page: 'agent-outreach' } },
      { path: 'settings/company-profile', component: SystemFormComponent, data: { title: 'Settings', page: 'company-profile' } },
      { path: 'settings/system', component: SystemFormComponent, data: { title: 'Settings', page: 'system' } },
      { path: 'settings/dashboard-settings', component: SystemFormComponent, data: { title: 'Settings', page: 'dashboard-settings' } },
      // Company Profile & Branding (mockup) includes the official signature and the logos.
      { path: 'settings/official-signature', redirectTo: 'settings/company-profile' },
      { path: 'settings/branding', redirectTo: 'settings/company-profile' },
      // E-Signature Providers (mockup): Adobe Sign and DocuSign on one page. The OAuth callbacks
      // still return to the old URLs, which open it.
      { path: 'settings/e-signatures', component: SignIntegrationComponent, data: { title: 'Settings' } },
      { path: 'settings/adobe-sign', redirectTo: 'settings/e-signatures' },
      { path: 'settings/docusign', redirectTo: 'settings/e-signatures' },
      { path: 'settings/lockout', component: LockoutSettingsComponent, data: { title: 'Settings' } },
      { path: 'settings/:key', component: SettingsPageComponent, data: { title: 'Settings' } },
      // Settings pages moved from twin-ci (who may use them: the API checks, as twin-ci's menu did).
      { path: 'chemicals', component: ChemicalsComponent, data: { title: 'Settings' } },
      { path: 'lead-sources', component: LeadSourcesComponent, data: { title: 'Settings' } },
      { path: 'branches', component: BranchesComponent, data: { title: 'Settings' } },
      { path: 'roles-permissions', component: RolesPermissionsComponent, data: { title: 'Settings' } },
      { path: 'users', component: UsersListComponent, data: { title: 'Settings' } },
      { path: 'users/add', component: UserFormComponent, data: { title: 'Settings' } },
      // twin-ci's calendar callbacks return administrators here (NEW_SITE_APP . 'users/<id>').
      { path: 'users/:id', component: UserFormComponent, data: { title: 'Settings' } },
      // Settings > My Account (Profile, Password & Preferences); the old URL still works.
      { path: 'my-account', component: ChangePasswordComponent, data: { title: 'Settings' } },
      { path: 'change-password', redirectTo: 'my-account' },
      // Settings > Database Import & Export (administrators only; the API checks). twin-ci's old
      // export / import redirects land on /data-export and /data-import, which open it.
      { path: 'data-exchange', component: DataExchangePageComponent, data: { title: 'Settings' } },
      { path: 'data-export', redirectTo: 'data-exchange' },
      { path: 'data-import', redirectTo: 'data-exchange' },
      // Settings > Communications: Appointment Emails & Canned Replies, and SMTP Settings.
      { path: 'auto-emails', component: AutoEmailsComponent, data: { title: 'Settings' } },
      { path: 'smtp-settings', component: SmtpSettingsComponent, data: { title: 'Settings' } },
      { path:'groupwidget/:groupId', component:GroupWidgetsComponent },
      // Widget Groups are part of Settings > Users (mockup).
      { path:'share-widgets', redirectTo: 'users' },
      { path:'sort-widgets', component:SotableWidgetComponent },
      { path:'new-app-widget', component:NewAppWidgetComponent },
      { path:'report-settings', component:ReportSettingsComponent },
      { path:'findings-report-settings', component:InspectionReportSettingComponent },
      { path:'billing-report-settings', component:BillingReportSettingComponent },
      // Canned Replies are part of Appointment Emails & Canned Replies; /canned-replies/:id opens
      // that page with the reply's edit dialog, and the old edit page's URL still works.
      { path:'canned-replies', redirectTo: 'auto-emails' },
      { path:'canned-replies/:id', component:AutoEmailsComponent, data: { title: 'Settings' } },
      { path:'edit-canned-reply/:id', redirectTo: 'canned-replies/:id' },
      { path:'rodents', component:DiagramComponent, data: { title: 'Freeform Diagram Editor' } },
      { path:'rodents/:id', component:DiagramComponent, data: { title: 'Freeform Diagram Editor' } },
      { path: 'clients', loadChildren: './modules/client/client.module#ClientModule' },
      { path: 'agencies', loadChildren: './modules/agency/agency.module#AgencyModule' },
      { path: 'codes-list', loadChildren: './modules/codes/codes.module#CodesModule' },
      { path: 'import-users', loadChildren: './modules/user-import/user-import.module#UserImportModule' },
      { path: 'import-zipcodes', loadChildren: './modules/zip-import/zip-import.module#ZipImportModule' },
      { path: 'revenue-types', loadChildren: './modules/revenue-types/revenue-types.module#RevenueTypesModule' },
      { path: 'document-library', loadChildren: './modules/document-library/document-library.module#DocumentLibraryModule' },
      { path: 'calendar-settings', loadChildren: './modules/calendar-settings/calendar-settings.module#CalendarSettingsModule' },
      { path: 'findings-settings', loadChildren: './modules/findings-settings/findings-settings.module#FindingsSettingsModule' },
      { path: 'milo', children: [
        { path: 'auth', component: ChatAuthComponent },
        { path: 'prompts/index', component: PromptsComponent, canActivate: [ChatAuthGuard] },
        { path: ':id', component: ChatMainComponent, canActivate: [ChatAuthGuard] },
        { path: '', component: ChatMainComponent, canActivate: [ChatAuthGuard] }
      ]},
      // Report pages (modules/reports), lazy loaded. Last, so every route above wins first; the
      // report paths stay at the top level (/accounting-report-job-costing, /new-clients ...).
      { path: '', loadChildren: './modules/reports/reports.module#ReportsModule' },
    ]}
];

@NgModule({
  exports: [ RouterModule ],
  imports: [ RouterModule.forRoot(routes) ],
})
export class AppRoutingModule {}
